import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { inventoryService, jobService } from "../services/database";
import AlertModal, { AlertIcons } from "../components/AlertModal";
import type { Job, JobEquipment, InventoryItem } from "../types/database";

interface ScannedItem {
  asset: any;
  scannedAt: Date;
  status:
    | "expected"
    | "unexpected"
    | "over-quantity"
    | "added-at-checkout"
    | "checked-out";
  expectedQuantity?: number;
  scannedQuantity: number;
  jobEquipmentId?: string;
}

interface AssetMenuProps {
  onViewScanned: () => void;
  onViewDetails: () => void;
  scannedAssets: ScannedItem[];
}

function AssetMenu({
  onViewScanned,
  onViewDetails,
  scannedAssets,
}: AssetMenuProps) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="p-1 rounded-full hover:bg-gray-100 transition-colors"
      >
        <svg
          className="w-5 h-5 text-gray-500"
          fill="currentColor"
          viewBox="0 0 20 20"
        >
          <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
        </svg>
      </button>

      {isOpen && (
        <>
          <div
            className="fixed inset-0 z-10"
            onClick={() => setIsOpen(false)}
          />
          <div className="absolute right-0 bottom-0 mb-8 w-48 bg-white rounded-md shadow-lg border border-gray-200 z-20">
            <div className="py-1">
              <button
                onClick={() => {
                  onViewScanned();
                  setIsOpen(false);
                }}
                disabled={scannedAssets.length === 0}
                className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
              >
                View Scanned Assets ({scannedAssets.length})
              </button>
              <button
                onClick={() => {
                  onViewDetails();
                  setIsOpen(false);
                }}
                className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
              >
                View Item Details
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default function Picklist() {
  const { currentOrganization, user } = useAuth();
  const { jobId, orgId } = useParams<{ jobId: string; orgId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [barcodeInput, setBarcodeInput] = useState("");
  const [scannedItems, setScannedItems] = useState<ScannedItem[]>([]);
  const [showScannedModal, setShowScannedModal] = useState(false);
  const [selectedAssetForDetails, setSelectedAssetForDetails] =
    useState<InventoryItem | null>(null);

  // Alert modal state
  const [alertModal, setAlertModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    type: "success" | "error" | "warning" | "info";
    icon?: React.ReactNode;
    showCancel?: boolean;
    onConfirm?: () => void;
  }>({
    isOpen: false,
    title: "",
    message: "",
    type: "info",
    showCancel: false,
  });

  // Fetch selected job details
  const { data: selectedJob, isLoading: jobLoading } = useQuery<Job>({
    queryKey: ["job", jobId],
    queryFn: () => jobService.getJob(jobId!, currentOrganization?.id),
    enabled: !!jobId && !!currentOrganization?.id,
  });

  // Fetch all inventory items to get names and images
  const { data: inventoryItems = [] } = useQuery<InventoryItem[]>({
    queryKey: ["inventory", currentOrganization?.id],
    queryFn: () => inventoryService.getInventoryItems(currentOrganization?.id),
    enabled: !!currentOrganization?.id,
  });

  // Fetch assets for validation and to get checked out assets
  const { data: assets = [] } = useQuery({
    queryKey: ["assets", currentOrganization?.id],
    queryFn: () => inventoryService.getAllAssets(currentOrganization?.id),
    enabled: !!currentOrganization?.id,
  });

  // Initialize scanned items from job's checked out assets
  useEffect(() => {
    if (
      selectedJob?.assets &&
      selectedJob.assets.length > 0 &&
      assets.length > 0
    ) {
      const checkedOutAssets = assets.filter((asset) =>
        selectedJob.assets.includes(asset.id)
      );

      const initialScannedItems: ScannedItem[] = checkedOutAssets.map(
        (asset) => {
          const expectedEquipment = selectedJob.equipment?.find(
            (equipment) => equipment.id === asset.parent_item_id
          );

          return {
            asset,
            scannedAt: new Date(),
            status: expectedEquipment ? "expected" : "unexpected",
            expectedQuantity: expectedEquipment?.quantity,
            scannedQuantity: 1,
            jobEquipmentId: expectedEquipment?.id,
          };
        }
      );

      setScannedItems(initialScannedItems);
    }
  }, [selectedJob, assets]);

  // Mutation for updating job assets
  const updateJobAssetsMutation = useMutation({
    mutationFn: async ({
      jobId,
      assetIds,
    }: {
      jobId: string;
      assetIds: string[];
    }) => {
      return jobService.updateJob(
        jobId,
        { assets: assetIds },
        currentOrganization?.id || ""
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["job", jobId] });
    },
  });

  // Mutation for updating job equipment
  const updateJobEquipmentMutation = useMutation({
    mutationFn: async ({
      jobId,
      equipment,
    }: {
      jobId: string;
      equipment: JobEquipment[];
    }) => {
      return jobService.updateJob(
        jobId,
        { equipment },
        currentOrganization?.id || ""
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["job", jobId] });
    },
  });

  // Get job equipment with full inventory item details
  const jobEquipmentWithDetails =
    selectedJob?.equipment?.map((equipment: JobEquipment) => {
      const inventoryItem = inventoryItems.find(
        (item) => item.id === equipment.id
      );

      // Check if this item was added at checkout (quantity 0)
      const isAddedAtCheckout = equipment.quantity === 0;

      return {
        ...equipment,
        inventoryItem,
        displayName: inventoryItem?.name || `Item ${equipment.id}`,
        displayImage: inventoryItem?.images?.[0] || null,
        displayCategory: inventoryItem?.category || "Unknown",
        isAddedAtCheckout,
      };
    }) || [];

  // Helper function to show alerts
  const showAlert = (
    title: string,
    message: string,
    type: "success" | "error" | "warning" | "info" = "info",
    icon?: React.ReactNode,
    showCancel = false,
    onConfirm?: () => void
  ) => {
    setAlertModal({
      isOpen: true,
      title,
      message,
      type,
      icon,
      showCancel,
      onConfirm,
    });
  };

  const closeAlert = () => {
    setAlertModal((prev) => ({ ...prev, isOpen: false }));
  };

  // Function to checkout an asset
  const checkoutAsset = async (assetId: string) => {
    try {
      // Update asset status to checked_out
      await inventoryService.updateAsset(
        assetId,
        { status: "checked_out" },
        currentOrganization?.id || ""
      );

      // Add to job's assets array
      const currentAssets = selectedJob?.assets || [];
      const updatedAssets = [...currentAssets, assetId];

      await updateJobAssetsMutation.mutateAsync({
        jobId: jobId!,
        assetIds: updatedAssets,
      });

      // Create checkout record
      await inventoryService.addCheckoutRecord({
        asset_id: assetId,
        checkout_date: new Date().toISOString(),
        return_date: null,
        job: jobId || null,
        checked_out_by: user?.id || "",
        created_by: user?.id || "",
        organization_id: currentOrganization?.id || "",
      });

      console.log(`Asset ${assetId} checked out successfully`);
    } catch (error) {
      console.error("Error checking out asset:", error);
      showAlert(
        "Checkout Failed",
        "Failed to checkout asset. Please try again.",
        "error",
        AlertIcons.error
      );
    }
  };

  // Handle barcode scan/input with automatic checkout
  const handleBarcodeSubmit = async (barcode: string) => {
    if (!jobId) {
      showAlert(
        "No Job Selected",
        "Please select a job before scanning assets.",
        "error",
        AlertIcons.error
      );
      return;
    }

    if (!barcode.trim()) {
      return;
    }

    // Find the asset by barcode
    const asset = assets.find((a) => a.barcode === barcode);

    if (!asset) {
      showAlert(
        "Asset Not Found",
        `No asset found with barcode: ${barcode}`,
        "error",
        AlertIcons.error
      );
      setBarcodeInput("");
      return;
    }

    // Check if asset is already checked out for this job
    const isAlreadyCheckedOut = selectedJob?.assets?.includes(asset.id);
    if (isAlreadyCheckedOut) {
      showAlert(
        "Asset Already Checked Out",
        `Asset ${barcode} is already checked out for this job.`,
        "warning",
        AlertIcons.warning
      );
      setBarcodeInput("");
      return;
    }

    // Check if asset is already scanned in current session
    const existingScannedItem = scannedItems.find(
      (item) => item.asset.barcode === barcode
    );

    if (existingScannedItem) {
      showAlert(
        "Asset Already Scanned",
        `Asset ${barcode} has already been scanned in this session.`,
        "warning",
        AlertIcons.warning
      );
      setBarcodeInput("");
      return;
    }

    // Check if this asset's parent_item_id is expected in the job
    const expectedEquipment = jobEquipmentWithDetails.find(
      (equipment) => equipment.id === asset.parent_item_id
    );

    let newScannedItem: ScannedItem;

    if (expectedEquipment) {
      // Asset is expected
      newScannedItem = {
        asset,
        scannedAt: new Date(),
        status: "expected",
        expectedQuantity: expectedEquipment.quantity,
        scannedQuantity: 1,
        jobEquipmentId: expectedEquipment.id,
      };
    } else {
      // Asset is not expected - ask user if they want to add it
      showAlert(
        "Unexpected Asset",
        `Asset ${barcode} is not assigned to this job. Would you like to add it to the checkout?`,
        "warning",
        AlertIcons.warning,
        true,
        async () => {
          // User confirmed - add the asset
          newScannedItem = {
            asset,
            scannedAt: new Date(),
            status: "added-at-checkout",
            scannedQuantity: 1,
          };

          setScannedItems((prev) => [...prev, newScannedItem]);
          setBarcodeInput("");

          // Add the inventory item to job equipment with quantity 0
          const currentEquipment = selectedJob?.equipment || [];
          const inventoryItemExists = currentEquipment.find(
            (item) => item.id === asset.parent_item_id
          );

          if (!inventoryItemExists) {
            const updatedEquipment = [
              ...currentEquipment,
              {
                id: asset.parent_item_id,
                quantity: 0,
              },
            ];

            await updateJobEquipmentMutation.mutateAsync({
              jobId: jobId!,
              equipment: updatedEquipment,
            });
          }

          // Automatically checkout the asset
          await checkoutAsset(asset.id);
        }
      );
      setBarcodeInput("");
      return;
    }

    setScannedItems((prev) => [...prev, newScannedItem]);
    setBarcodeInput("");

    // Automatically checkout the asset
    await checkoutAsset(asset.id);
  };

  // Handle form submission
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    handleBarcodeSubmit(barcodeInput);
  };

  // Handle keyboard input for barcode scanner
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.key === "Enter" && barcodeInput.trim()) {
        handleBarcodeSubmit(barcodeInput);
      }
    };

    document.addEventListener("keypress", handleKeyPress);
    return () => document.removeEventListener("keypress", handleKeyPress);
  }, [barcodeInput]);

  // Get scanned quantity for an inventory item
  const getScannedQuantity = (inventoryItemId: string) => {
    return scannedItems
      .filter((item) => item.asset.parent_item_id === inventoryItemId)
      .reduce((sum, item) => sum + item.scannedQuantity, 0);
  };

  // Get progress for job equipment
  const getEquipmentProgress = (equipment: any) => {
    const scannedQty = getScannedQuantity(equipment.id);
    const expectedQty = equipment.quantity;

    // For items added at checkout (quantity 0), show scanned quantity as progress
    if (equipment.isAddedAtCheckout) {
      return {
        scanned: scannedQty,
        expected: 0, // Show 0 as expected for added at checkout items
        isComplete: scannedQty > 0, // Complete if any scanned
        isOverQuantity: false,
        remaining: 0,
      };
    }

    const isComplete = scannedQty >= expectedQty;
    const isOverQuantity = scannedQty > expectedQty;

    return {
      scanned: scannedQty,
      expected: expectedQty,
      isComplete,
      isOverQuantity,
      remaining: Math.max(0, expectedQty - scannedQty),
    };
  };

  // Clear all scanned items
  const clearScannedItems = () => {
    setScannedItems([]);
  };

  // Get scanned assets for a specific inventory item
  const getScannedAssetsForItem = (inventoryItemId: string) => {
    return scannedItems.filter(
      (item) => item.asset.parent_item_id === inventoryItemId
    );
  };

  // Get checked out assets for a specific inventory item
  const getCheckedOutAssetsForItem = (inventoryItemId: string) => {
    if (!selectedJob?.assets || !assets.length) return [];
    return assets.filter(
      (asset) =>
        asset.parent_item_id === inventoryItemId &&
        selectedJob.assets.includes(asset.id)
    );
  };

  if (jobLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!selectedJob) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <h2 className="text-xl font-medium text-gray-900 mb-4">
            Job not found
          </h2>
          <button
            onClick={() => navigate(`/org/${orgId}/picklist`)}
            className="bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary/90"
          >
            Back to Job Selection
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Picklist</h1>
          <p className="mt-1 text-sm text-gray-500">{selectedJob.title}</p>
        </div>
        <button
          onClick={() => navigate(`/org/${orgId}/picklist`)}
          className="text-primary hover:text-primary/80 font-medium"
        >
          ← Back to Jobs
        </button>
      </div>

      {/* Scanner */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="max-w-md mx-auto">
          <h2 className="text-lg font-medium text-gray-900 mb-4 text-center">
            Scan Barcode
          </h2>

          <form onSubmit={handleSubmit} className="space-y-4">
            <input
              type="text"
              value={barcodeInput}
              onChange={(e) => setBarcodeInput(e.target.value)}
              placeholder="Scan or type barcode..."
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary text-lg"
              autoFocus
            />

            <button
              type="submit"
              disabled={!barcodeInput.trim()}
              className="w-full bg-primary text-white py-2 px-4 rounded-lg hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Scan & Checkout
            </button>
          </form>
        </div>
      </div>

      {/* Equipment List */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-medium text-gray-900">
              Equipment ({jobEquipmentWithDetails.length})
            </h2>
            {scannedItems.length > 0 && (
              <button
                onClick={clearScannedItems}
                className="text-sm text-red-600 hover:text-red-900 px-3 py-1 rounded-md hover:bg-red-50"
              >
                Clear All
              </button>
            )}
          </div>
        </div>

        <div className="max-h-96 overflow-y-auto">
          {jobEquipmentWithDetails.length === 0 ? (
            <div className="p-8 text-center">
              <svg
                className="mx-auto h-12 w-12 text-gray-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1}
                  d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
                />
              </svg>
              <p className="mt-2 text-sm text-gray-500">
                No equipment assigned to this job
              </p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {jobEquipmentWithDetails.map((equipment) => {
                const progress = getEquipmentProgress(equipment);
                const scannedAssets = getScannedAssetsForItem(equipment.id);
                const checkedOutAssets = getCheckedOutAssetsForItem(
                  equipment.id
                );

                return (
                  <div
                    key={equipment.id}
                    className={`p-4 ${
                      progress.isComplete ? "bg-green-50" : ""
                    } ${progress.isOverQuantity ? "bg-red-50" : ""} ${
                      equipment.isAddedAtCheckout
                        ? "bg-blue-50 border-l-4 border-blue-400"
                        : ""
                    }`}
                  >
                    <div className="flex items-center space-x-3">
                      {/* Equipment Image */}
                      <div className="flex-shrink-0 h-12 w-12">
                        {equipment.displayImage ? (
                          <img
                            src={equipment.displayImage}
                            alt={equipment.displayName}
                            className="h-12 w-12 object-cover rounded-lg border border-gray-200"
                          />
                        ) : (
                          <div
                            className={`h-12 w-12 rounded-lg flex items-center justify-center border-2 ${
                              progress.isComplete
                                ? "bg-green-100 border-green-200 text-green-600"
                                : progress.isOverQuantity
                                ? "bg-red-100 border-red-200 text-red-600"
                                : "bg-gray-100 border-gray-200 text-gray-500"
                            }`}
                          >
                            <svg
                              className="w-6 h-6"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
                              />
                            </svg>
                          </div>
                        )}
                      </div>

                      {/* Equipment Details */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <h3 className="text-base font-medium text-gray-900">
                              {equipment.displayName}
                            </h3>
                            {equipment.displayCategory && (
                              <p className="text-sm text-gray-500">
                                {equipment.displayCategory}
                              </p>
                            )}
                          </div>

                          {/* Progress Status */}
                          <div className="flex items-center space-x-3 ml-4">
                            <div className="text-right">
                              <div
                                className={`text-xl font-bold ${
                                  progress.isComplete
                                    ? "text-green-600"
                                    : progress.isOverQuantity
                                    ? "text-red-600"
                                    : "text-gray-700"
                                }`}
                              >
                                {equipment.isAddedAtCheckout
                                  ? `${progress.scanned} / 0`
                                  : `${progress.scanned} / ${progress.expected}`}
                              </div>
                              <div className="text-xs text-gray-500">
                                scanned
                              </div>
                            </div>

                            {/* Status Icon */}
                            <div
                              className={`p-1.5 rounded-full ${
                                progress.isComplete
                                  ? "bg-green-100 text-green-600"
                                  : progress.isOverQuantity
                                  ? "bg-red-100 text-red-600"
                                  : "bg-gray-100 text-gray-500"
                              }`}
                            >
                              {progress.isComplete ? (
                                <svg
                                  className="w-4 h-4"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  stroke="currentColor"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M5 13l4 4L19 7"
                                  />
                                </svg>
                              ) : progress.isOverQuantity ? (
                                <svg
                                  className="w-4 h-4"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  stroke="currentColor"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                                  />
                                </svg>
                              ) : (
                                <svg
                                  className="w-4 h-4"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  stroke="currentColor"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
                                  />
                                </svg>
                              )}
                            </div>

                            {/* 3-dot menu */}
                            <AssetMenu
                              onViewScanned={() => {
                                if (
                                  scannedAssets.length > 0 ||
                                  checkedOutAssets.length > 0
                                ) {
                                  setShowScannedModal(true);
                                }
                              }}
                              onViewDetails={() => {
                                if (equipment.inventoryItem) {
                                  setSelectedAssetForDetails(
                                    equipment.inventoryItem as any
                                  );
                                }
                              }}
                              scannedAssets={[
                                ...scannedAssets,
                                ...checkedOutAssets.map((asset) => ({
                                  asset,
                                  scannedAt: new Date(),
                                  status: "checked-out" as any,
                                  scannedQuantity: 1,
                                })),
                              ]}
                            />
                          </div>
                        </div>

                        {/* Progress Details */}
                        <div className="mt-2 flex items-center space-x-4">
                          {equipment.isAddedAtCheckout && (
                            <span className="text-xs text-blue-600 font-medium bg-blue-100 px-2 py-1 rounded-full">
                              ➕ Added at Checkout
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Scanned Assets Modal */}
      {showScannedModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-96 overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium">Scanned Assets</h3>
              <button
                onClick={() => setShowScannedModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            <div className="space-y-3">
              {scannedItems.map((item, index) => (
                <div
                  key={index}
                  className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg"
                >
                  <div className="flex-1">
                    <div className="font-medium">
                      Barcode: {item.asset.barcode}
                    </div>
                    <div className="text-sm text-gray-500">
                      {item.status === "checked-out"
                        ? "Already checked out"
                        : `Scanned: ${item.scannedAt.toLocaleTimeString()}`}
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="text-sm text-gray-600">
                      Qty: {item.scannedQuantity}
                    </div>
                    {item.status === "checked-out" && (
                      <span className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full">
                        Checked Out
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Asset Details Modal */}
      {selectedAssetForDetails && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-96 overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium">Item Details</h3>
              <button
                onClick={() => setSelectedAssetForDetails(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <h4 className="font-medium">Name</h4>
                <p className="text-gray-600">{selectedAssetForDetails.name}</p>
              </div>
              <div>
                <h4 className="font-medium">Category</h4>
                <p className="text-gray-600">
                  {selectedAssetForDetails.category}
                </p>
              </div>
              {selectedAssetForDetails.description && (
                <div>
                  <h4 className="font-medium">Description</h4>
                  <p className="text-gray-600">
                    {selectedAssetForDetails.description}
                  </p>
                </div>
              )}
              {selectedAssetForDetails.manufacturer && (
                <div>
                  <h4 className="font-medium">Manufacturer</h4>
                  <p className="text-gray-600">
                    {selectedAssetForDetails.manufacturer}
                  </p>
                </div>
              )}
              {selectedAssetForDetails.model && (
                <div>
                  <h4 className="font-medium">Model</h4>
                  <p className="text-gray-600">
                    {selectedAssetForDetails.model}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Alert Modal */}
      <AlertModal
        isOpen={alertModal.isOpen}
        onClose={closeAlert}
        title={alertModal.title}
        message={alertModal.message}
        type={alertModal.type}
        icon={alertModal.icon}
        showCancel={alertModal.showCancel}
        onConfirm={alertModal.onConfirm}
      />
    </div>
  );
}
